import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { adminService } from '../../services/adminService';
import Card, { CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import {
  UserGroupIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<{
    users: number;
    pendingCreators: number;
    totalPosts: number;
    flaggedPosts: number;
    totalRevenue: number;
    failedPayments: number;
  } | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const s = await adminService.getStats();
        setStats(s);
      } catch (err: any) {
        console.error('Failed to load admin stats:', err.response?.data || err.message);
        // Fallback to minimal stats if call fails
        try {
          const users = await adminService.listUsers({ limit: 1 });
          const pending = await adminService.listPendingCreators();
          setStats({
            users: users.pagination?.total || 0,
            pendingCreators: pending.creators?.length || 0,
            totalPosts: 0,
            flaggedPosts: 0,
            totalRevenue: 0,
            failedPayments: 0,
          });
        } catch (fallbackErr: any) {
          console.error('Failed to load fallback stats:', fallbackErr.response?.data || fallbackErr.message);
          setStats(null);
        }
      }
    };
    load();
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Admin Console</h1>
      
      {stats === null && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-yellow-800">Failed to load admin statistics. Check browser console for details.</p>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0 }}
          className="relative overflow-hidden rounded-xl shadow-lg"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-cyan-600 opacity-90"></div>
          <div className="relative backdrop-blur-sm bg-white/20 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-white/30 rounded-lg backdrop-blur-sm">
                <UserGroupIcon className="w-6 h-6 text-white" />
              </div>
              <ArrowTrendingUpIcon className="w-5 h-5 text-white/80" />
            </div>
            <h3 className="text-3xl font-bold text-white">{stats?.users ?? '—'}</h3>
            <p className="text-white/90 text-sm mt-1 font-medium">Total Users</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="relative overflow-hidden rounded-xl shadow-lg"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-green-500 to-emerald-600 opacity-90"></div>
          <div className="relative backdrop-blur-sm bg-white/20 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-white/30 rounded-lg backdrop-blur-sm">
                <ShieldCheckIcon className="w-6 h-6 text-white" />
              </div>
              {stats?.pendingCreators && stats.pendingCreators > 0 && (
                <span className="bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-bold shadow-lg">
                  {stats.pendingCreators}
                </span>
              )}
            </div>
            <h3 className="text-3xl font-bold text-white">{stats?.pendingCreators ?? '—'}</h3>
            <p className="text-white/90 text-sm mt-1 font-medium">Pending Verifications</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="relative overflow-hidden rounded-xl shadow-lg"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-red-500 to-pink-600 opacity-90"></div>
          <div className="relative backdrop-blur-sm bg-white/20 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-white/30 rounded-lg backdrop-blur-sm">
                <DocumentTextIcon className="w-6 h-6 text-white" />
              </div>
              {stats?.flaggedPosts && stats.flaggedPosts > 0 && (
                <span className="bg-red-700 text-white px-2 py-1 rounded-full text-xs font-bold shadow-lg">
                  Alert
                </span>
              )}
            </div>
            <h3 className="text-3xl font-bold text-white">{stats?.flaggedPosts ?? '—'}</h3>
            <p className="text-white/90 text-sm mt-1 font-medium">Flagged Posts</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="relative overflow-hidden rounded-xl shadow-lg"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-indigo-600 opacity-90"></div>
          <div className="relative backdrop-blur-sm bg-white/20 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-white/30 rounded-lg backdrop-blur-sm">
                <CurrencyDollarIcon className="w-6 h-6 text-white" />
              </div>
              <ChartBarIcon className="w-5 h-5 text-white/80" />
            </div>
            <h3 className="text-3xl font-bold text-white">
              {stats?.totalRevenue !== undefined
                ? `$${stats.totalRevenue.toFixed(2)}`
                : '—'}
            </h3>
            <p className="text-white/90 text-sm mt-1 font-medium">Total Revenue</p>
          </div>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3, delay: 0.4 }}
        >
          <Card className="hover:shadow-xl hover:border-blue-200 transition-all duration-300">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">User Management</h2>
                <p className="text-gray-600">Suspend/activate users and manage roles</p>
              </div>
              <Link to="/admin/users"><Button>Open</Button></Link>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3, delay: 0.45 }}>
          <Card className="hover:shadow-xl hover:border-green-200 transition-all duration-300">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Creator Verification</h2>
                <p className="text-gray-600">Approve creator profiles</p>
              </div>
              <Link to="/admin/creators"><Button>Open</Button></Link>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3, delay: 0.5 }}>
          <Card className="hover:shadow-xl hover:border-purple-200 transition-all duration-300">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Subscriptions</h2>
                <p className="text-gray-600">Monitor all platform subscriptions</p>
              </div>
              <Link to="/admin/subscriptions"><Button>Open</Button></Link>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3, delay: 0.55 }}>
          <Card className="hover:shadow-xl hover:border-indigo-200 transition-all duration-300">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Membership Tiers</h2>
                <p className="text-gray-600">View and manage creator tiers</p>
              </div>
              <Link to="/admin/tiers"><Button>Open</Button></Link>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3, delay: 0.6 }}>
          <Card className="hover:shadow-xl hover:border-red-200 transition-all duration-300">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Content Moderation</h2>
                <p className="text-gray-600">Review and moderate posts</p>
              </div>
              <Link to="/admin/posts"><Button>Open</Button></Link>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3, delay: 0.65 }}>
          <Card className="hover:shadow-xl hover:border-blue-200 transition-all duration-300">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Comments</h2>
                <p className="text-gray-600">Moderate user comments</p>
              </div>
              <Link to="/admin/comments"><Button>Open</Button></Link>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3, delay: 0.7 }}>
          <Card className="hover:shadow-xl hover:border-yellow-200 transition-all duration-300">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Reports</h2>
                <p className="text-gray-600">Review and manage content reports</p>
              </div>
              <Link to="/admin/reports"><Button>Open</Button></Link>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3, delay: 0.75 }}>
          <Card className="hover:shadow-xl hover:border-emerald-200 transition-all duration-300">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Payment Management</h2>
                <p className="text-gray-600">Monitor transactions and refunds</p>
              </div>
              <Link to="/admin/payments"><Button>Open</Button></Link>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3, delay: 0.8 }}>
          <Card className="hover:shadow-xl hover:border-cyan-200 transition-all duration-300">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Analytics</h2>
                <p className="text-gray-600">Platform performance and metrics</p>
              </div>
              <Link to="/admin/analytics"><Button>Open</Button></Link>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3, delay: 0.85 }}>
          <Card className="hover:shadow-xl hover:border-pink-200 transition-all duration-300">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Coupons</h2>
                <p className="text-gray-600">Create and manage discount coupons</p>
              </div>
              <Link to="/admin/coupons"><Button>Open</Button></Link>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3, delay: 0.9 }}>
          <Card className="hover:shadow-xl hover:border-teal-200 transition-all duration-300">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">KYC Review</h2>
                <p className="text-gray-600">Review and approve KYC documents</p>
              </div>
              <Link to="/admin/kyc"><Button>Open</Button></Link>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3, delay: 0.95 }}>
          <Card className="hover:shadow-xl hover:border-violet-200 transition-all duration-300">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Payouts</h2>
                <p className="text-gray-600">Manage creator payouts</p>
              </div>
              <Link to="/admin/payouts"><Button>Open</Button></Link>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3, delay: 1.0 }}>
          <Card className="hover:shadow-xl hover:border-orange-200 transition-all duration-300">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Webhooks</h2>
                <p className="text-gray-600">Manage external integrations</p>
              </div>
              <Link to="/admin/webhooks"><Button>Open</Button></Link>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3, delay: 1.05 }}>
          <Card className="hover:shadow-xl hover:border-slate-300 transition-all duration-300">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Audit Logs</h2>
                <p className="text-gray-600">Track administrative actions</p>
              </div>
              <Link to="/admin/audit-logs"><Button>Open</Button></Link>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3, delay: 1.1 }}>
          <Card className="hover:shadow-xl hover:border-purple-300 transition-all duration-300">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Platform Branding</h2>
                <p className="text-gray-600">Customize logo, name, and favicon</p>
              </div>
              <Link to="/admin/branding"><Button>Open</Button></Link>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3, delay: 1.15 }}>
          <Card className="hover:shadow-xl hover:border-purple-300 transition-all duration-300">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Platform Settings</h2>
                <p className="text-gray-600">Configure platform features and fees</p>
              </div>
              <Link to="/admin/settings"><Button>Open</Button></Link>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3, delay: 1.15 }}>
          <Card className="hover:shadow-xl hover:border-red-300 transition-all duration-300">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">System Monitoring</h2>
                <p className="text-gray-600">System health and error logs</p>
              </div>
              <Link to="/admin/system"><Button>Open</Button></Link>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminDashboard;
